"use client"

import styled from "@emotion/styled"
import { yupResolver } from "@hookform/resolvers/yup"
import { Box, TextField } from "@mui/material"
import { postWebBmbyLead } from "api"
import { useLocale } from "next-intl"
import Image from "next/image"
import { LegacyRef, useEffect, useRef, useState } from "react"
import ReCAPTCHA from "react-google-recaptcha"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { useInView } from "react-intersection-observer"
import { extractProperty, getUmbracoMediaUrl, replaceRichTextLinks } from "utils"
import { useLandingAnalytics } from "../analytics"
import { generateLandingLeadFormSchema } from "utils/Form/FormSchema"
import * as yup from "yup"

type Props = {
  data: any
  bmbyId?: number
  companyId?: number
}

// eslint-disable-next-line prefer-destructuring
const RECAPTCHA_KEY = process.env.RECAPTCHA_KEY
if (!RECAPTCHA_KEY) {
  console.warn("RECAPTCHA_KEY not set — form bot protection disabled")
}

// --- Styled Components ---

const Section = styled.section`
  position: relative;
  padding: 40px 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 64px 120px;
  }

  ${(props) => props.theme.breakpoints.up("xl")} {
    padding: 64px 240px;
  }
`

const BgImage = styled.div`
  position: absolute;
  inset: 0;
  z-index: -1;

  img {
    object-fit: cover;
  }
`

const Card = styled.div<{ $inView: boolean }>`
  background: var(--comp-bg, #414141);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 550px;
  opacity: ${({ $inView }) => ($inView ? 1 : 0)};
  transform: ${({ $inView }) => ($inView ? "translateY(0)" : "translateY(30%)")};
  transition: opacity 0.6s ease, transform 0.6s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    border-radius: 20px;
    padding: 40px;
  }
`

const HeaderArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  text-align: center;
`

const Headline = styled.h2`
  font-size: 40px;
  font-weight: 700;
  line-height: 40px;
  letter-spacing: -0.8px;
  color: var(--comp-heading, white);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
    line-height: 1.2;
  }
`

const Subtitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 1.5;
  color: var(--comp-text, white);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
    font-weight: 400;
    letter-spacing: -0.4px;
  }
`

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;

  ${(props) => props.theme.breakpoints.up("md")} {
    width: 470px;
    max-width: 700px;
    align-self: center;
  }

  /* Remove autofill background color */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 30px white inset !important;
    -webkit-text-fill-color: #020a0d !important;
    transition: background-color 5000s ease-in-out 0s;
  }

  .MuiTextField-root,
  .MuiFormControl-root {
    display: flex;
    flex-direction: column;
    background: none !important;
    background-color: transparent !important;
    position: relative;
    margin-bottom: 32px;
  }

  /* Label styling - absolute, hidden by default, visible only on error */
  .MuiInputLabel-root {
    position: absolute;
    top: -18px;
    right: 0;
    transform: none;
    color: #878b9d;
    font-size: 14px;
    font-weight: 400;
    pointer-events: none;
    display: none;

    &.Mui-focused {
      color: #878b9d;
    }

    &.Mui-error {
      display: block;
      color: #878b9d;
    }

    &.MuiInputLabel-shrink {
      transform: none;
    }
  }

  /* Standard Input styling for TextField */
  .MuiInput-root {
    background-color: white !important;
    border: 1px solid #878b9d;
    border-radius: 8px;
    padding: 14px;
    height: 48px;
    margin-top: 0 !important;
    overflow: hidden;

    &:before,
    &:after {
      display: none;
    }

    &.Mui-focused {
      border-color: #878b9d;
      outline: 2px solid #b5bfc9;
      outline-offset: 2px;
    }

    &.Mui-error {
      border-color: #ff1626;
    }

    .MuiInput-input {
      padding: 0;
      font-size: 16px;
      color: #020a0d;

      &::placeholder {
        color: #726f6f;
        opacity: 1;
      }
    }
  }

  /* Helper text (error messages) — absolute so it doesn't shift layout.
     The .Mui-error selector is needed to beat MUI's own error colour (#d32f2f)
     so field errors match the checkbox error colour. */
  .MuiFormHelperText-root,
  .MuiFormHelperText-root.Mui-error {
    color: white;
    font-size: 14px;
    font-weight: 500;
    margin-inline-start: 0;
    position: absolute;
    bottom: -22px;
  }
`

const ConsentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MarketingText = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: var(--comp-text, white);
  line-height: 1.5;

  p {
    margin-block: 0;
  }

  a {
    color: var(--comp-text, white);
    text-decoration: underline;
  }
`

const CheckboxField = styled.div`
  position: relative;
`

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
`

const CheckboxLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  cursor: pointer;
`

const CheckboxBox = styled.div<{ $checked?: boolean; $error?: boolean }>`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.$error ? "#FF1626" : "var(--comp-text, white)")};
  background: ${(props) => (props.$checked ? "var(--comp-text, white)" : "transparent")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;

  ${HiddenCheckbox}:focus-visible + ${CheckboxLabel} > & {
    outline: 2px dashed var(--comp-text, white);
    outline-offset: 2px;
  }
`

const CheckMark = styled.svg`
  width: 16px;
  height: 16px;
`

const CheckboxText = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: var(--comp-text, white);
  line-height: 1.5;
  flex: 1;

  p {
    margin-block: 0;
  }

  a {
    color: var(--comp-text, white);
    text-decoration: underline;
  }
`

const ErrorText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: white;
`

const SubmitButton = styled.button`
  height: 48px;
  background: var(--inner-bg, white);
  color: var(--inner-text, black);
  border: 1px solid var(--inner-text, black);
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: opacity 0.15s;

  &:hover {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text, white);
    outline-offset: 2px;
  }
`

const StateContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  text-align: center;
  min-height: 300px;
`

const StateLogo = styled.div`
  width: 100px;
  height: 100px;
  position: relative;
`

const StateTitle = styled.h2`
  font-size: 40px;
  font-weight: 700;
  color: var(--comp-heading, white);
  margin: 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const StateMessage = styled.p`
  font-size: 18px;
  font-weight: 400;
  color: var(--comp-text, white);
  margin: 0;
  max-width: 500px;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 20px;
  }
`

const RetryButton = styled.button`
  height: 48px;
  background: var(--inner-bg, white);
  color: var(--inner-text, black);
  border: 1px solid var(--inner-text, black);
  border-radius: 8px;
  font-size: 20px;
  font-weight: 700;
  cursor: pointer;
  padding: 0 32px;

  &:hover {
    opacity: 0.85;
  }

  &:focus-visible {
    outline: 2px dashed var(--comp-text, white);
    outline-offset: 2px;
  }
`

// --- Custom Dropdown Components ---

const DropdownWrapper = styled.div`
  position: relative;
  width: 100%;
  z-index: 10;
  /* Matches the TextField spacing: extra room when the error message is shown. */
  margin-bottom: 32px;
`

/* White, like the TextField helper text and the consent ErrorText - red on the
   form's dark surface fails contrast (ASH-2192). */
const DropdownError = styled.span`
  position: absolute;
  bottom: -22px;
  inset-inline-start: 0;
  font-size: 14px;
  font-weight: 500;
  color: white;
`

const DropdownTrigger = styled.button<{ $isOpen: boolean; $hasValue: boolean }>`
  width: 100%;
  height: 48px;
  background: white;
  border: 1px solid ${(props) => (props.$isOpen ? "#B5BFC9" : "#878B9D")};
  border-radius: ${(props) => (props.$isOpen ? "12px 12px 0 0" : "8px")};
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  font-size: 16px;
  color: ${(props) => (props.$hasValue ? "#020A0D" : "#726F6F")};
  text-align: start;
  transition: border-radius 0.15s, border-color 0.15s;

  &:focus-visible {
    outline: 2px solid #b5bfc9;
    outline-offset: 2px;
    border-color: #b5bfc9;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`

const DropdownArrow = styled.span<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  transform: ${(props) => (props.$isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  flex-shrink: 0;
`

const DropdownPanel = styled.div`
  position: absolute;
  top: 100%;
  inset-inline: 0;
  background: white;
  border: 1.2px solid #b5bfc9;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0px 4px 12px rgba(42, 42, 42, 0.12);
  max-height: 240px;
  overflow-y: auto;
  z-index: 11;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #b5bfc9;
    border-radius: 200px;
  }

  /* Firefox scrollbar */
  scrollbar-width: thin;
  scrollbar-color: #b5bfc9 transparent;
`

const DropdownItem = styled.button<{ $isSelected: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 16px;
  color: #020a0d;
  font-weight: ${(props) => (props.$isSelected ? 700 : 400)};
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: start;

  &:hover {
    background: #f5f5f5;
  }
`

const DropdownLabel = styled.label`
  color: var(--comp-text, white);
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 8px;
  display: block;
`

// --- Chevron SVG ---

const ChevronIcon = () => (
  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M1 1.5L6 6.5L11 1.5"
      stroke="#726F6F"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

// --- Helpers ---

/** Bmby expects a positive numeric id; -1 is its "not provided" sentinel. */
const toBmbyId = (value: unknown): number => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : -1
}

// --- Component ---

function LeadForm({ data, bmbyId: pageBmbyId, companyId: pageCompanyId }: Props) {
  const locale = useLocale()
  const { ref: cardInViewRef, inView: cardInView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  })
  const reCaptchaRef = useRef<ReCAPTCHA | null>()

  const analytics = useLandingAnalytics()
  const [formState, setFormState] = useState<"form" | "success" | "error">("form")

  // The success/error states hold far less content than the form, so the card would
  // collapse and shift the rest of the page. Freeze the form's height before leaving it.
  const cardRef = useRef<HTMLDivElement>(null)
  const [lockedHeight, setLockedHeight] = useState<number>()

  // CMS fields
  const headline = extractProperty(data, "leadFormHeadline") as string | ""
  const subtitle = extractProperty(data, "leadFormSubtitle") as string | ""
  const buttonText = (extractProperty(data, "leadFormButtonText") as string) || "שליחה"
  // Single background image — uses desktop field as primary, mobile as fallback for existing content
  const bgDesktopMedia = extractProperty(data, "leadFormBgDesktop", true) as any[] | undefined
  const bgMobileMedia = extractProperty(data, "leadFormBgMobile", true) as any[] | undefined
  const leadFormProjects = extractProperty(data, "leadFormProjects") as string[] | "" | undefined
  const leadFormEmail = (extractProperty(data, "leadFormEmail") as string) || ""
  const marketingText = extractProperty(data, "leadFormMarketingText") as string | ""
  const termsText = extractProperty(data, "leadFormTermsText") as string | ""
  const newsletterText = extractProperty(data, "leadFormNewsletterText") as string | ""

  // Success state fields
  const successLogoMedia = extractProperty(data, "leadFormSuccessLogo", true) as any[] | undefined
  const successTitle = extractProperty(data, "leadFormSuccessTitle") as string | ""
  const successMessage = extractProperty(data, "leadFormSuccessMessage") as string | ""

  // Error state fields
  const errorLogoMedia = extractProperty(data, "leadFormErrorLogo", true) as any[] | undefined
  const errorTitle = extractProperty(data, "leadFormErrorTitle") as string | ""
  const errorMessage = extractProperty(data, "leadFormErrorMessage") as string | ""
  const errorButtonText = (extractProperty(data, "leadFormErrorButtonText") as string) || "נסה שוב"

  // Page-level fields — passed from MarketingLandingPage, fallback to block data.
  // extractProperty yields "" for unset properties, so normalise to Bmby's -1 sentinel.
  const bmbyId = toBmbyId(pageBmbyId || extractProperty(data, "bmbyId"))
  const companyId = toBmbyId(pageCompanyId || extractProperty(data, "companyId"))

  const bgUrl = bgDesktopMedia?.[0]?.url || bgMobileMedia?.[0]?.url || ""

  const hasProjects = Array.isArray(leadFormProjects) && leadFormProjects.length > 0

  // Field-level mandatory toggles from CMS (default true for backwards compatibility)
  const fullNameRequired = extractProperty(data, "leadFormFullNameRequired") !== false
  const phoneRequired = extractProperty(data, "leadFormPhoneRequired") !== false
  const emailRequired = extractProperty(data, "leadFormEmailRequired") !== false

  // Form setup
  const lang = locale === "he" ? "he" : "en"
  const schema = generateLandingLeadFormSchema(lang, hasProjects, {
    fullName: fullNameRequired,
    email: emailRequired,
    phone: phoneRequired,
  })
  type FormInput = yup.InferType<typeof schema>

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitted, isSubmitting },
    reset,
  } = useForm<FormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      project: "",
      fullName: "",
      email: "",
      phone: "",
      acceptTerms: !termsText,
      allowMarketing: false,
    },
  })

  // Custom dropdown state
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // After submit, focus the first field that has an error
  const fieldOrder: (keyof FormInput)[] = [
    ...(hasProjects ? ["project" as keyof FormInput] : []),
    "fullName",
    "phone",
    "email",
    "acceptTerms",
  ]

  const onInvalid = (fieldErrors: Record<string, any>) => {
    const firstErrorField = fieldOrder.find((f) => f in fieldErrors)
    /* error_name is the message the visitor actually sees, e.g. the spec's
       "לא מולאו כל השדות" / "טלפון לא תקין" (ASH-1854) */
    analytics.formError(
      buttonText,
      (firstErrorField && fieldErrors[firstErrorField]?.message) || "",
    )
    if (!firstErrorField) return
    if (firstErrorField === "project") {
      setIsDropdownOpen(true)
      dropdownRef.current?.querySelector<HTMLButtonElement>("button")?.focus()
      return
    }
    document.getElementById(`lead-form-${firstErrorField}`)?.focus()
  }

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isDropdownOpen])

  const onSubmit: SubmitHandler<FormInput> = async (formData) => {
    reCaptchaRef?.current?.reset()
    const recaptchaResponse = (await reCaptchaRef?.current?.executeAsync()) as string
    if (!recaptchaResponse) return

    // Capture the form height before the card swaps to a success/error state
    if (cardRef.current) setLockedHeight(cardRef.current.offsetHeight)

    try {
      const senddata = {
        projectId: String(bmbyId),
        name: formData.fullName || "",
        email: formData.email,
        phone: formData.phone,
        comments: "",
        gRecaptchaResponse: recaptchaResponse,
        projectStatus: "MarketingAndRental" as string,
        allowedMarketing: formData.allowMarketing || false,
        allowedSmsAndEmail: formData.allowMarketing || false,
        acceptedTerms: formData.acceptTerms,
        companyId,
        uri: window?.location?.href,
        toEmail: leadFormEmail,
        projectw: formData.project || "",
      }
      const response = await postWebBmbyLead(senddata as any)
      if (response?.data?.body?.addLeadSucceeded) {
        analytics.formSuccess(buttonText)
        setFormState("success")
      } else {
        analytics.formError(buttonText, "lead submission failed")
        setFormState("error")
      }
    } catch {
      analytics.formError(buttonText, "lead submission failed")
      setFormState("error")
    }
  }

  const handleRetry = () => {
    reset()
    setLockedHeight(undefined)
    setFormState("form")
  }

  const successLogoUrl = successLogoMedia?.[0]?.url || ""
  const errorLogoUrl = errorLogoMedia?.[0]?.url || ""

  return (
    <Section ref={cardInViewRef}>
      {bgUrl && (
        <BgImage>
          <Image
            src={getUmbracoMediaUrl(bgUrl)}
            fill
            alt=""
            style={{ objectFit: "cover" }}
            priority
          />
        </BgImage>
      )}

      <Card
        ref={cardRef}
        $inView={cardInView}
        style={formState === "form" ? undefined : { minHeight: lockedHeight }}
      >
        {formState === "success" ? (
          <StateContainer>
            {successLogoUrl && (
              <StateLogo>
                <Image
                  src={getUmbracoMediaUrl(successLogoUrl)}
                  fill
                  alt=""
                  style={{ objectFit: "contain" }}
                />
              </StateLogo>
            )}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {successTitle && <StateTitle>{successTitle}</StateTitle>}
              {successMessage && <StateMessage>{successMessage}</StateMessage>}
            </Box>
          </StateContainer>
        ) : formState === "error" ? (
          <StateContainer>
            {errorLogoUrl && (
              <StateLogo>
                <Image
                  src={getUmbracoMediaUrl(errorLogoUrl)}
                  fill
                  alt=""
                  style={{ objectFit: "contain" }}
                />
              </StateLogo>
            )}

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {errorTitle && <StateTitle>{errorTitle}</StateTitle>}
              {errorMessage && <StateMessage role="alert">{errorMessage}</StateMessage>}
              <RetryButton type="button" onClick={handleRetry}>
                {errorButtonText}
              </RetryButton>
            </Box>
          </StateContainer>
        ) : (
          <>
            <HeaderArea>
              {headline && <Headline>{headline}</Headline>}
              {subtitle && <Subtitle>{subtitle}</Subtitle>}
            </HeaderArea>

            <StyledForm onSubmit={handleSubmit(onSubmit, onInvalid)}>
              {/* Project dropdown — Custom component */}
              {hasProjects && (
                <Controller
                  name="project"
                  control={control}
                  render={({ field }) => (
                    <DropdownWrapper ref={dropdownRef}>
                      <DropdownLabel>*באיזה פרויקט אתה מתעניין?</DropdownLabel>
                      <DropdownTrigger
                        type="button"
                        $isOpen={isDropdownOpen}
                        $hasValue={!!field.value}
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                        aria-expanded={isDropdownOpen}
                        aria-haspopup="listbox"
                        aria-required
                        aria-invalid={!!errors.project}
                        aria-describedby={errors.project ? "lead-form-project-error" : undefined}
                      >
                        <span>{field.value || "*באיזה פרויקט אתה מתעניין?"}</span>
                        <DropdownArrow $isOpen={isDropdownOpen}>
                          <ChevronIcon />
                        </DropdownArrow>
                      </DropdownTrigger>
                      {isDropdownOpen && (
                        <DropdownPanel role="listbox">
                          {(leadFormProjects as string[]).map((project, idx) => (
                            <DropdownItem
                              key={idx}
                              type="button"
                              role="option"
                              aria-selected={field.value === project}
                              $isSelected={field.value === project}
                              onClick={() => {
                                field.onChange(project)
                                setIsDropdownOpen(false)
                              }}
                            >
                              <span>{project}</span>
                            </DropdownItem>
                          ))}
                        </DropdownPanel>
                      )}
                      {isSubmitted && errors.project && (
                        <DropdownError id="lead-form-project-error" role="alert">
                          {errors.project.message}
                        </DropdownError>
                      )}
                    </DropdownWrapper>
                  )}
                />
              )}

              {/* Full Name — MUI TextField (standard) */}
              <Controller
                name="fullName"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="lead-form-fullName"
                    label={fullNameRequired ? "*שם מלא" : "שם מלא"}
                    placeholder={fullNameRequired ? "*שם מלא" : "שם מלא"}
                    type="text"
                    variant="standard"
                    error={!!errors.fullName}
                    helperText={isSubmitted && errors.fullName?.message}
                    fullWidth
                    autoComplete="name"
                    InputLabelProps={{ shrink: true, htmlFor: "lead-form-fullName" }}
                    inputProps={{
                      "aria-required": fullNameRequired,
                      "aria-invalid": !!errors.fullName,
                      "aria-describedby": errors.fullName ? "lead-form-fullName-error" : undefined,
                    }}
                    FormHelperTextProps={{ role: "alert", id: "lead-form-fullName-error" } as any}
                  />
                )}
              />

              {/* Phone — MUI TextField (standard) */}
              <Controller
                name="phone"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="lead-form-phone"
                    label={phoneRequired ? "*מספר טלפון" : "מספר טלפון"}
                    placeholder={phoneRequired ? "*מספר טלפון" : "מספר טלפון"}
                    type="tel"
                    variant="standard"
                    error={!!errors.phone}
                    helperText={isSubmitted && errors.phone?.message}
                    fullWidth
                    autoComplete="tel"
                    inputProps={{
                      dir: "rtl",
                      "aria-required": phoneRequired,
                      "aria-invalid": !!errors.phone,
                      "aria-describedby": errors.phone ? "lead-form-phone-error" : undefined,
                    }}
                    InputLabelProps={{ shrink: true, htmlFor: "lead-form-phone" }}
                    FormHelperTextProps={{ role: "alert", id: "lead-form-phone-error" } as any}
                  />
                )}
              />

              {/* Email — MUI TextField (standard) */}
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="lead-form-email"
                    label={emailRequired ? "*אימייל" : "אימייל"}
                    placeholder={emailRequired ? "*אימייל" : "אימייל"}
                    type="email"
                    variant="standard"
                    error={!!errors.email}
                    helperText={isSubmitted && errors.email?.message}
                    fullWidth
                    autoComplete="email"
                    InputLabelProps={{ shrink: true, htmlFor: "lead-form-email" }}
                    inputProps={{
                      "aria-required": emailRequired,
                      "aria-invalid": !!errors.email,
                      "aria-describedby": errors.email ? "lead-form-email-error" : undefined,
                    }}
                    FormHelperTextProps={{ role: "alert", id: "lead-form-email-error" } as any}
                  />
                )}
              />

              {/* Consent Area */}
              <ConsentArea>
                {marketingText && (
                  <MarketingText
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(marketingText, true),
                    }}
                  />
                )}

                {/* Terms checkbox (required) */}
                {termsText && (
                  <Controller
                    name="acceptTerms"
                    control={control}
                    render={({ field }) => (
                      <CheckboxField>
                        <HiddenCheckbox
                          type="checkbox"
                          id="lead-form-acceptTerms"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          aria-required="true"
                          aria-describedby={
                            errors.acceptTerms ? "lead-form-acceptTerms-error" : undefined
                          }
                        />
                        <CheckboxLabel htmlFor="lead-form-acceptTerms">
                          <CheckboxBox
                            $checked={!!field.value}
                            $error={!!errors.acceptTerms}
                            aria-hidden="true"
                          >
                            {field.value && (
                              <CheckMark viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path
                                  d="M3 8L6.5 11.5L13 4.5"
                                  stroke="#414141"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </CheckMark>
                            )}
                          </CheckboxBox>
                          <CheckboxText
                            dangerouslySetInnerHTML={{
                              __html: replaceRichTextLinks(termsText, true),
                            }}
                          />
                        </CheckboxLabel>
                        {errors.acceptTerms && (
                          <ErrorText id="lead-form-acceptTerms-error" role="alert">
                            {errors.acceptTerms.message}
                          </ErrorText>
                        )}
                      </CheckboxField>
                    )}
                  />
                )}

                {/* Newsletter checkbox (optional) */}
                {newsletterText && (
                  <Controller
                    name="allowMarketing"
                    control={control}
                    render={({ field }) => (
                      <CheckboxField>
                        <HiddenCheckbox
                          type="checkbox"
                          id="lead-form-allowMarketing"
                          checked={!!field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                        <CheckboxLabel htmlFor="lead-form-allowMarketing">
                          <CheckboxBox $checked={!!field.value} aria-hidden="true">
                            {field.value && (
                              <CheckMark viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                <path
                                  d="M3 8L6.5 11.5L13 4.5"
                                  stroke="#414141"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </CheckMark>
                            )}
                          </CheckboxBox>
                          <CheckboxText
                            dangerouslySetInnerHTML={{
                              __html: replaceRichTextLinks(newsletterText, true),
                            }}
                          />
                        </CheckboxLabel>
                      </CheckboxField>
                    )}
                  />
                )}
              </ConsentArea>

              {/* Submit */}
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "שולח..." : buttonText}
              </SubmitButton>
            </StyledForm>
          </>
        )}
      </Card>
      {RECAPTCHA_KEY && (
        <ReCAPTCHA
          ref={reCaptchaRef as LegacyRef<ReCAPTCHA> | undefined}
          sitekey={RECAPTCHA_KEY}
          size="invisible"
          hl="iw"
          tabIndex={-1}
        />
      )}
    </Section>
  )
}

export default LeadForm
